import React, { useState, useEffect, useMemo, useCallback, ChangeEvent } from 'react';
import { Modal as ModalComponent } from 'components/modal/Modal';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import FormTextField from 'components/form/FormTextField';
import { WrappedFormSingleSelectField } from 'components/form/FormSingleSelectField';
import Button from 'components/button/Button';
import { Spinner } from 'components/spinner/Spinner';
import * as Yup from 'yup';
import instance, { ApiResponse } from 'api';
import { endpoints } from 'endpoints.config';
import { Toast, ToastMessageReason } from 'helpers/toast';
import { useSelector, useDispatch } from 'react-redux';
import { selectComponentResources } from 'reducers/componentResources';
import { closeModal } from 'reducers/modal';
import { getCustomerComponentResources } from 'reducers/componentResources';
import { RadioButtons } from 'components/radiobuttons/radiobuttons';
import { FormMultiSelectField } from 'components/form/FormMultiSelectField';
import { CheckboxInput } from 'components/form/CheckboxInput';
import FormElements from 'components/categoryHelpers/FormElements';
import { toCamelCase } from 'helpers/formatFormFieldNames';
import UploadField from 'components/FormBuilder/UploadField';
import PDFViewer from 'components/pdf/PDFViewer';
import { cloneDeep } from 'lodash';

type Step = 'loading' | 'merchant-questions' | 'card-details' | 'doc-upload' | 'agreements' | 'pending' | 'error';

interface FieldMetadata {
    name: string;
    type: string;
}

type MerchantQuestionsFormValues = {
    bAcceptCardPayments: boolean;
    customerTrafficCountries: string[];
    historicalChargebackRate: string;
    historicalRefundRate: string;
    productsServicesSold: string;
};

type CardDetailsFormValues = {
    businessActivityType?: string;
    complianceFieldOptions: any;
    currency?: string;
    customerSubtype?: string;
    occupationType?: string;
    sourceOfFundsType?: string;
    taxNumber?: string;
    shippingMethod: string;
    product?: string;
    cardType?: string;
    sameAsRegisteredAddress?: boolean;
    [key: string]: any;
};

type DocumentUploadFormValues = {
    Type: string;
    DocumentSide: string;
    file: File | null;
};

type AgreementsFormValues = {
    acceptConsent: boolean;
    acceptCHA: boolean;
};

const merchantValidationSchema = Yup.object({
    bAcceptCardPayments: Yup.boolean().required('Required'),
    customerTrafficCountries: Yup.array().min(1, 'Select at least one country'),
    historicalChargebackRate: Yup.number().min(0, 'Must be positive').max(100, 'Must be 0-100').required('Required'),
    historicalRefundRate: Yup.number().min(0, 'Must be positive').max(100, 'Must be 0-100').required('Required'),
    productsServicesSold: Yup.string().required('Required'),
});

export const RequestCardModal = () => {
    const dispatch = useDispatch();
    const onClose = () => dispatch(closeModal());
    const { userInfo } = useSelector(selectComponentResources);
    
    const [step, setStep] = useState<Step>('loading');
    const [formData, setFormData] = useState<any>(null);
    const [merchantData, setMerchantData] = useState<MerchantQuestionsFormValues | null>(null);
    const [cardDetailsData, setCardDetailsData] = useState<CardDetailsFormValues | null>(null);
    const [documentUploaded, setDocumentUploaded] = useState(false);
    const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
    const [countries, setCountries] = useState<any[]>([]);
    const [isSameAsRegisteredAddress, setIsSameAsRegisteredAddress] = useState(true);

    const isBusinessAccount = userInfo?.accountType === 'Business';

    // Load form data on mount
    useEffect(() => {
        const loadFormData = async () => {
            try {
                const response = await instance.get<ApiResponse<any>>(
                    endpoints.cards.reviewcreatecard
                );

                if (response.data.status !== '1') {
                    setStep('error');
                    return;
                }

                setFormData(response.data);

                // Load countries for Business
                if (isBusinessAccount) {
                    const countriesRes = await instance.get<ApiResponse<any>>(
                        endpoints.businessVerificationV3Module.getDropdownData
                    );
                    if (countriesRes.data.status === '1') {
                        setCountries(countriesRes.data.details?.availableCountries || []);
                    }
                }

                // Set initial step
                // Check if request already pending
                const statusRes = await instance.get<ApiResponse<any>>(
                    endpoints.cards.checkcardrequeststatus
                );

                if (statusRes.data.status === '1' && statusRes.data.details?.bPending) {
                    setStep('pending');
                    return;
                }

                // Set initial step
                setStep(isBusinessAccount ? 'merchant-questions' : 'card-details');
            } catch (err) {
                console.error('Error loading form data:', err);
                setStep('error');
            }
        };

        loadFormData();
    }, [isBusinessAccount]);

    // ==================== MERCHANT QUESTIONS ====================
    const merchantInitialValues: MerchantQuestionsFormValues = {
        bAcceptCardPayments: false,
        customerTrafficCountries: [],
        historicalChargebackRate: '',
        historicalRefundRate: '',
        productsServicesSold: '',
    };

    const handleMerchantSubmit = (
        values: MerchantQuestionsFormValues,
        { setSubmitting }: FormikHelpers<MerchantQuestionsFormValues>
    ) => {
        setMerchantData(values);
        setStep('card-details');
        setSubmitting(false);
    };

    // ==================== CARD DETAILS ====================
    const cardDetailsInitialValues: CardDetailsFormValues = useMemo(() => {
        if (!formData?.details) return {
            complianceFieldOptions: {},
            shippingMethod: '',
            cardType: '1',
            sameAsRegisteredAddress: false,
        };

        const camelCasedDetails = Object.keys(formData.details).reduce((acc, key) => {
            acc[toCamelCase(key)] = formData.details[key];
            return acc;
        }, {} as Record<string, any>);

        const metadata = formData.metadata as FieldMetadata[];
        const sameAsField: FieldMetadata = {
            name: 'sameAsRegisteredAddress',
            type: 'Boolean',
        };

        const extendedMetadata: FieldMetadata[] = [];
        (metadata || []).forEach((field) => {
            if (field.name === 'ShippingAddressLine1') {
                extendedMetadata.push(sameAsField);
            }
            extendedMetadata.push(field);
        });

        const mappedFields = extendedMetadata.reduce((prev, field) => {
            const key = toCamelCase(field.name);
            switch (field.type.toLowerCase()) {
                case 'string':
                case 'text':
                    prev[key] = '';
                    break;
                case 'number':
                case 'integer':
                    prev[key] = null;
                    break;
                case 'boolean':
                    prev[key] = false;
                    break;
                case 'multiselect':
                    prev[key] = [];
                    break;
                default:
                    prev[key] = null;
            }
            return prev;
        }, {} as Record<string, any>);

        return {
            complianceFieldOptions: {},
            shippingMethod: '',
            product: formData.details?.product || '',
            ...mappedFields,
            ...camelCasedDetails,
            cardType: '1',
            sameAsRegisteredAddress: false,
        } as CardDetailsFormValues;
    }, [formData]);

    const handleCardDetailsSubmit = (
        values: CardDetailsFormValues,
        { setSubmitting }: FormikHelpers<CardDetailsFormValues>
    ) => {
        setCardDetailsData(values);
        
        if (documentUploaded || values.bDocUploaded || formData?.details?.bDocUploaded) {
            setStep('agreements');
        } else {
            setStep('doc-upload');
        }
        setSubmitting(false);
    };

    const overrideFormFieldRenderer = {
        shippingMethod: (_key: string | number, field: any) => (
            <div className="FormBox">
                <div className="FormLabel">
                    <label>{field.label || 'Shipping Method'}</label>
                </div>
                <RadioButtons
                    options={['Standard', 'Express']}
                    fieldname="shippingMethod"
                    label=""
                />
            </div>
        ),
    };

    // ==================== DOCUMENT UPLOAD ====================
    const documentInitialValues: DocumentUploadFormValues = {
        Type: 'Tax_ID',
        DocumentSide: 'Front',
        file: null,
    };

    const handleDocumentSubmit = async (
        values: DocumentUploadFormValues,
        { setSubmitting }: FormikHelpers<DocumentUploadFormValues>
    ) => {
        if (!values.file) {
            Toast.openToastMessage('Please upload the document', ToastMessageReason.ERROR);
            setSubmitting(false);
            return;
        }

        const formDataToUpload = new FormData();
        formDataToUpload.append('Type', values.Type);
        formDataToUpload.append('DocumentSide', values.DocumentSide);
        formDataToUpload.append('File', values.file);

        try {
            await instance.post(endpoints.cards.documentupload, formDataToUpload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setDocumentUploaded(true);
            setStep('agreements');
        } catch (err) {
            Toast.openToastMessage('Document upload failed', ToastMessageReason.ERROR);
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== AGREEMENTS ====================
    const agreementsInitialValues: AgreementsFormValues = {
        acceptConsent: false,
        acceptCHA: false,
    };

    const handleScroll = (event: any) => {
        const target = event.target;
        const scrollY = target.scrollTop;
        const innerHeight = target.clientHeight;
        const scrollHeight = target.scrollHeight;
        if (scrollY + innerHeight + 10 >= scrollHeight) {
            setIsScrolledToEnd(true);
        }
    };

    const handleFinalSubmit = async () => {
        try {
            const payload = {
                ...cardDetailsData,
                merchantQuestions: isBusinessAccount ? merchantData : null,
                cardType: cardDetailsData?.cardType === '1' ? 'Physical' : 'Virtual',
                BusinessRegistrationNumber: '123',
                PubliclyListed: false,
                LicenseRequired: false,
            };

            const response = await instance.post<ApiResponse<any>>(
                endpoints.cards.requestCard, // NEW ENDPOINT
                payload
            );

            if (response.data.status === '1') {
                Toast.openSuccessToast('Card request submitted successfully! Awaiting approval.');
                setStep('pending');
                dispatch(getCustomerComponentResources());
            } else {
                Toast.openErrorToast('Failed to submit request');
            }
        } catch (err) {
            console.error('Error submitting card request:', err);
            Toast.openErrorToast('Failed to submit request');
        }
    };

    // ==================== RENDER STEPS ====================
    const getTitle = () => {
        switch (step) {
            case 'merchant-questions': return 'Merchant Services Information';
            case 'card-details': return 'Card Request Details';
            case 'doc-upload': return 'Document Upload';
            case 'agreements': return 'Cardholder Agreement';
            case 'pending': return 'Request Submitted';
            case 'error': return 'Error';
            default: return 'Request Card';
        }
    };

    const getPDFPath = () => {
        const product = cardDetailsData?.product || formData?.details?.product;
        
        if (product === 'CrossRiverBank') {
            return isBusinessAccount 
                ? "/e-sign/Commercial_Deposit_Account_Agreement.pdf"
                : "/e-sign/Consumer_Agreement_BaaS.pdf";
        } else if (product === 'Girasol') {
            return "/e-sign/GIRASOL PAYMENT SOLUTIONS DEBIT MASTERCARD CARDHOLDER AGREEMENT (1).docx.pdf";
        }
        return null;
    };

    return (
        <ModalComponent
            title={getTitle()}
            onCloseModal={onClose}
            className="RequestCardModal"
        >
            {/* LOADING */}
            {step === 'loading' && (
                <div className="RequestCardLoading">
                    <Spinner />
                    <p>Loading form...</p>
                </div>
            )}

            {/* ERROR */}
            {step === 'error' && (
                <div className="RequestCardState">
                    <h3>Something went wrong</h3>
                    <p>Unable to load card request form. Please try again later.</p>
                    <Button variety="full" onClick={onClose}>Close</Button>
                </div>
            )}

            {/* PENDING */}
            {step === 'pending' && (
                <div className="RequestCardState">
                    <h3>Request Under Review</h3>
                    <p>
                        Your card request is currently under review by our compliance team. 
                        We'll notify you once the review is complete.
                    </p>
                    <Button variety="full" onClick={onClose}>Close</Button>
                </div>
            )}

            {/* MERCHANT QUESTIONS (Business Only) */}
            {step === 'merchant-questions' && (
                <Formik
                    initialValues={merchantInitialValues}
                    validationSchema={merchantValidationSchema}
                    onSubmit={handleMerchantSubmit}
                >
                    {({ isSubmitting, values }) => (
                        <Form>
                            <p style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
                                Please provide information about your merchant services.
                            </p>

                            <RadioButtons
                                options={['Yes', 'No']}
                                fieldname="bAcceptCardPayments"
                                label="Do you currently accept card payments? *"
                                booleanField
                            />

                            <FormMultiSelectField
                                fieldName="customerTrafficCountries"
                                label="Countries Where You Have Customer Traffic *"
                                options={countries.map((c: any) => ({
                                    label: c.countryName,
                                    value: c.isoCode,
                                }))}
                            />

                            <div className="FormSideBySide">
                                <FormTextField
                                    field="historicalChargebackRate"
                                    label="Historical Chargeback Rate (%) *"
                                    placeholder="e.g., 0.5"
                                    type="number"
                                    sideBySide
                                />
                                <FormTextField
                                    field="historicalRefundRate"
                                    label="Historical Refund Rate (%) *"
                                    placeholder="e.g., 2"
                                    type="number"
                                    sideBySide
                                />
                            </div>

                            <FormTextField
                                type="textarea"
                                field="productsServicesSold"
                                label="Type of Products/Services Sold *"
                                placeholder="Describe what you sell"
                            />

                            <div className="FullWidth Buttons">
                                <Button
                                    variety="full"
                                    type="submit"
                                    disabled={isSubmitting}
                                    showSubmittingSpinner={isSubmitting}
                                >
                                    Continue
                                </Button>
                                <Button
                                    variety="full"
                                    priority="secondary"
                                    type="button"
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}

            {/* CARD DETAILS */}
            {step === 'card-details' && formData && (
                <Formik<CardDetailsFormValues>
                    initialValues={cardDetailsInitialValues}
                    enableReinitialize
                    onSubmit={handleCardDetailsSubmit}
                >
                    {(formik: FormikProps<CardDetailsFormValues>) => (
                        <Form className="FormBuilderForm">
                            {/* Card Type Selection */}
                            <div className="FormBox" style={{ width: '100%', marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 500, fontSize: '12px', marginBottom: '6px' }}>
                                    Card Type *
                                </label>
                                <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                                    {[
                                        { label: 'Physical', value: '1' },
                                        { label: 'Virtual', value: '2' },
                                    ].map((option) => (
                                        <label
                                            key={option.value}
                                            style={{
                                                flex: 1,
                                                textAlign: 'center',
                                                padding: '10px 12px',
                                                borderRadius: 4,
                                                border: formik.values.cardType === option.value
                                                    ? '2px solid #0052cc'
                                                    : '1px solid #D1D5DB',
                                                backgroundColor: formik.values.cardType === option.value
                                                    ? '#0052cc20'
                                                    : '#f2f2f3',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                fontWeight: formik.values.cardType === option.value ? '600' : '400',
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="cardType"
                                                value={option.value}
                                                checked={formik.values.cardType === option.value}
                                                onChange={formik.handleChange}
                                                style={{ display: 'none' }}
                                            />
                                            {option.label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Form Fields */}
                            <FormElements
                                values={formik.values}
                                data={{
                                    ...formData,
                                    metadata: formik.values.cardType === '2'
                                        ? (formData.metadata || []).filter(
                                            (f: any) =>
                                                !f.name.toLowerCase().includes('shipping') &&
                                                !f.name.toLowerCase().includes('sameasregisteredaddress')
                                        )
                                        : formData.metadata,
                                }}
                                loadingFallback={<Spinner />}
                                overrideFormFieldRenderer={overrideFormFieldRenderer}
                            />

                            <div className="FullWidth Buttons">
                                <Button
                                    variety="full"
                                    type="submit"
                                    disabled={formik.isSubmitting}
                                    showSubmittingSpinner={formik.isSubmitting}
                                >
                                    Continue
                                </Button>
                                <Button
                                    variety="full"
                                    priority="secondary"
                                    type="button"
                                    onClick={() => {
                                        if (isBusinessAccount) {
                                            setStep('merchant-questions');
                                        } else {
                                            onClose();
                                        }
                                    }}
                                >
                                    Back
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}

            {/* DOCUMENT UPLOAD */}
            {step === 'doc-upload' && (
                <Formik
                    initialValues={documentInitialValues}
                    onSubmit={handleDocumentSubmit}
                >
                    {({ isSubmitting }) => (
                        <Form className="FormBuilderForm">
                            <div className="FormBox">
                                <div className="FormLabel">
                                    <label>
                                        {isBusinessAccount
                                            ? 'Corporate Authorization Document *'
                                            : 'Tax Document *'}
                                    </label>
                                </div>
                                <UploadField
                                    fieldName="file"
                                    displayError={true}
                                    multi={false}
                                />
                            </div>

                            <div className="FullWidth Buttons">
                                <Button
                                    variety="full"
                                    type="submit"
                                    disabled={isSubmitting}
                                    showSubmittingSpinner={isSubmitting}
                                >
                                    Continue
                                </Button>
                                <Button
                                    variety="full"
                                    priority="secondary"
                                    type="button"
                                    onClick={() => setStep('card-details')}
                                >
                                    Back
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}

            {/* AGREEMENTS */}
            {step === 'agreements' && (
                <Formik
                    initialValues={agreementsInitialValues}
                    onSubmit={() => {}}
                >
                    {({ values, setFieldValue }) => (
                        <Form>
                            <div
                                style={{
                                    height: '300px',
                                    overflowY: 'auto',
                                    border: '1px solid #cccccc',
                                    borderRadius: '10px',
                                    marginBottom: '20px',
                                }}
                                onScroll={handleScroll}
                            >
                                {getPDFPath() && <PDFViewer fileUrl={getPDFPath()!} />}
                                <PDFViewer fileUrl="/e-sign/E-Sign_Disclosure_and_Consent_Agreement.pdf" />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <CheckboxInput
                                    disabled={!isScrolledToEnd}
                                    name="acceptConsent"
                                    label="I accept the E-Sign Consent *"
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                        setFieldValue('acceptConsent', e.target.checked)
                                    }
                                    checked={values.acceptConsent}
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <CheckboxInput
                                    disabled={!isScrolledToEnd}
                                    name="acceptCHA"
                                    label="I accept the Cardholder Agreement *"
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => 
                                        setFieldValue('acceptCHA', e.target.checked)
                                    }
                                    checked={values.acceptCHA}
                                />
                            </div>

                            <p style={{ color: 'red', fontSize: '12px', minHeight: '20px' }}>
                                {!isScrolledToEnd && 'Please read through the entire consent text to continue.'}
                            </p>

                            <div className="FullWidth Buttons">
                                <Button
                                    variety="full"
                                    type="button"
                                    disabled={!values.acceptConsent || !values.acceptCHA}
                                    onClick={handleFinalSubmit}
                                >
                                    Submit Request
                                </Button>
                                <Button
                                    variety="full"
                                    priority="secondary"
                                    type="button"
                                    onClick={() => setStep('doc-upload')}
                                >
                                    Back
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
        </ModalComponent>
    );
};